import { useMemo } from 'react';
import { fc, GeoJsonLayer } from './layers';

/** Requested stations as amber rings sized by their requests (distinct from the purple heatmap). */
export function DemandStationsLayer({
  stations,
  focus,
  onSelect,
}: {
  stations: Array<{ id: string; name: string; lng: number; lat: number; n: number }>;
  focus: string | null;
  onSelect: (id: string) => void;
}) {
  const data = useMemo(() => {
    const max = Math.max(1, ...stations.map((s) => s.n));
    return fc(
      stations
        .filter((s) => s.n > 0)
        .map((s) => ({
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [s.lng, s.lat] },
          properties: {
            id: s.id,
            name: s.name,
            n: s.n,
            r: Math.sqrt(s.n / max),
            focus: s.id === focus ? 1 : 0,
          },
        })),
    );
  }, [stations, focus]);
  return (
    <GeoJsonLayer
      data={data}
      onClick={(p) => typeof p.id === 'string' && onSelect(p.id)}
      layers={[
        {
          type: 'circle',
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['get', 'r'], 0, 5, 1, 22],
            'circle-color': '#f59e0b',
            'circle-opacity': 0.12,
            'circle-stroke-width': ['case', ['==', ['get', 'focus'], 1], 4, 2],
            'circle-stroke-color': ['case', ['==', ['get', 'focus'], 1], '#b45309', '#f59e0b'],
          } as never,
        },
      ]}
    />
  );
}

/** Where requests came from, one dot per ~100 m cell, larger with more requests. */
export function RequestDotsLayer({ cells }: { cells: Array<[number, number, number, number]> }) {
  const data = useMemo(() => {
    const max = Math.max(1, ...cells.map((c) => c[2]));
    return fc(
      cells.map((c) => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [c[0], c[1]] },
        properties: { n: c[2], r: Math.log1p(c[2]) / Math.log1p(max) },
      })),
    );
  }, [cells]);
  return (
    <GeoJsonLayer
      data={data}
      layers={[
        {
          type: 'circle',
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['get', 'r'], 0, 3, 1, 9],
            'circle-color': '#4f46e5',
            'circle-opacity': 0.85,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#ffffff',
          } as never,
        },
      ]}
    />
  );
}
